import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Card, { CardContent } from "./TakashiCard";
import SparklineChart from "./SparklineChart";
import watchlistData from "./watchlists.json";
import { fetchWatchlistStocks, fetchStockHistory, fetchStock, fetchTickerNews } from "../models/marketsService";
import SectionHeader from "./SectionHeader";
import WatchlistSummary from "../components/WatchlistSummary";
import TickerNewsManager from "../components/TickerNewsManager";
import Typography from "../components/Typography";
import { useTheme } from "../context/ThemeContext";


const swipeConfidenceThreshold = 10000;
const swipePower = (offset, velocity) => Math.abs(offset) * velocity;

const WatchlistPage = () => {
  const { theme } = useTheme();
  const { watchlistId } = useParams();
  const navigate = useNavigate();
  const [watchlists, setWatchlists] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [stockData, setStockData] = useState({});
  const [highlightedTickers, setHighlightedTickers] = useState({});

  useEffect(() => {
    const storedWatchlists = JSON.parse(localStorage.getItem("watchlists")) || watchlistData;
    setWatchlists(storedWatchlists);
    const initialIndex = storedWatchlists.findIndex(w => w.id === watchlistId);
    if (initialIndex !== -1) setCurrentIndex(initialIndex);

    fetchStockData(storedWatchlists[initialIndex]?.stocks || []);
  }, [watchlistId]);

  // **Fetch Stock Data from API**
  const fetchStockData = async (tickers) => {
    try {
      const response = await fetchWatchlistStocks(tickers.join(","));
      const formattedStocks = await Promise.all(response.map(async (stock) => {
      const { normalizedHistory, normalizedFuture } = await fetchStockHistory(stock.ticker);

      // Get today's high & low prices to denormalize values
      const minPrice = stock.day?.l || stock.min?.l || 0; // Low of the day
      const maxPrice = stock.day?.h || stock.min?.h || 1; // High of the day

      // ✅ Convert normalized history back to real prices
      const actualHistory = normalizedHistory.map(normValue => 
        minPrice + (normValue * (maxPrice - minPrice))
      );

      // ✅ Get the latest real price (either from history or API data)
      const latestHistoryPrice = actualHistory.length > 0 ? actualHistory[actualHistory.length - 1] : null;
      const latestPrice = latestHistoryPrice || stock.lastTrade?.p || stock.min?.c || stock.day?.c || 0;
        console.log(`Latest Price for ${stock.ticker}: ${latestPrice}`);

        let companyName = stock.name || stock.companyName || null;  
        
        // ✅ If company name is missing, fetch it separately
          if (!companyName) {
            try {
              const stockInfo = await fetchStock(stock.ticker);
              console.log(stockInfo.results);
              companyName = stockInfo?.results?.name || stock.ticker;

              // ✅ Truncate at first period (.) if it exists, otherwise cap at 20 characters
              companyName = companyName.includes(".")
                ? companyName.split(".")[0]
                : companyName.length > 20
                ? companyName.substring(0, 20) + "" // Ellipsis for long names
                : companyName;
                          } catch (error) {
              console.error(`Error fetching company name for ${stock.ticker}:`, error);
              companyName = stock.ticker; // Fallback
            }
          }
      
        return {
          ticker: stock.ticker || "N/A",
          name: companyName || "Unknown Company",
          price: latestPrice || 0,
          change: stock.todaysChangePerc || 0,
          history: normalizedHistory,
          future: normalizedFuture, // ✅ Store future line for dotted chart
        };
      }));

      const stockMap = formattedStocks.reduce((acc, stock) => {
        acc[stock.ticker] = stock;
        return acc;
      }, {});

      setStockData((prevStockData) => {
        const newStockData = { ...prevStockData };

        Object.keys(stockMap).forEach((ticker) => {
          const newPrice = stockMap[ticker].price;
          const oldPrice = prevStockData[ticker]?.price;

          if (oldPrice !== undefined && newPrice !== oldPrice) {
            const changeColor = newPrice > oldPrice ? "green" : "red";
          
            setHighlightedTickers((prev) => ({
              ...prev,
              [ticker]: changeColor, // Store green or red highlight
            }));
          
            setTimeout(() => {
              setHighlightedTickers((prev) => ({
                ...prev,
                [ticker]: "", // Reset back to default
              }));
            }, 1000);
          }
          
          

          newStockData[ticker] = stockMap[ticker];
        });

        return newStockData;
      });
    } catch (error) {
      console.error("Error fetching stock data:", error);
    }
  };

  // **Refresh Data Every 5 Seconds**
  useEffect(() => {
    const interval = setInterval(() => {
      if (watchlists[currentIndex]) {
        fetchStockData(watchlists[currentIndex].stocks);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [watchlists, currentIndex]);

  const paginate = (newDirection) => {
    let newIndex = currentIndex + newDirection;
    if (newIndex < 0) newIndex = watchlists.length - 1;
    else if (newIndex >= watchlists.length) newIndex = 0;
    setCurrentIndex(newIndex);
    navigate(`/markets/watchlist/${watchlists[newIndex].id}`);
  };

  return (
    <div className="watchlist-container" style={{ display: "flex", overflow: "hidden", width: "100%" }}>
      <AnimatePresence initial={false} custom={currentIndex} mode="popLayout">
        {watchlists.map((watchlist, index) => (
          <motion.div
            key={watchlist.id}
            initial={{ x: (index - currentIndex) * 100 + "%" }}
            animate={{ x: (index - currentIndex) * 100 + "%" }}
            exit={{ x: (index - currentIndex) * 100 + "%" }}
            transition={{
              type: "tween",
              duration: 1, 
              ease: [0.33, 1, 0.68, 1],
            }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            onDragEnd={(e, { offset, velocity }) => {
              const swipe = swipePower(offset.x, velocity.x);
              if (swipe < -swipeConfidenceThreshold) paginate(1);
              else if (swipe > swipeConfidenceThreshold) paginate(-1);
            }}
            style={{ flexShrink: 0, width: "100%", position: "absolute", top: 0, left: 0 }}
          >
            <div className="content">
              
              <WatchlistSummary watchlist={watchlist} />
              <SectionHeader title="Stocks" />

              <div className="stock-list">
                {watchlist.stocks.map(ticker => {
                  const stock = stockData[ticker] || {};
                  const highlightClass = highlightedTickers[ticker] === "green" 
                    ? "text-green-500 transition-all duration-500"
                    : highlightedTickers[ticker] === "red" 
                      ? "text-red-500 transition-all duration-500"
                      : "";

                  return (
                    <Card key={ticker} className="card">
                      <CardContent>
                        <div className="CompanyDetails">
                          <div className="ticker">
                            <div className={stock.change >= 0 ? "ticker-icon positive" : "ticker-icon negative"}>
                              {stock.change >= 0 ? "▲" : "▼"}
                            </div>
                            <h2 className="font-semibold">{ticker}</h2>
                          </div>
                          <p className="text-sm text-gray-500">{stock.name || "Unknown Stock"}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <SparklineChart 
                            data={stock.history || []} 
                            isPositive={stock.change >= 0} 
                            ticker={stock.ticker}
                          />
                          
                        </div>
                        <div className="Stock-Details">
                        <motion.p
                          className={`text-lg font-bold transition-colors duration-500 ${
                            highlightedTickers[ticker] === "green" 
                              ? "text-green-500" 
                              : highlightedTickers[ticker] === "red" 
                              ? "text-red-500" 
                              : "text-white" // Default color
                          }`}

                        >
                          ${stock.price ? stock.price.toFixed(2) : "N/A"}
                        </motion.p>


                          <p className={stock.change < 0 ? "text-red-500 font-semibold" : "text-green-500 font-semibold"}>
                            {stock.change ? stock.change.toFixed(2) + "%" : "N/A"}
                          </p>

                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              {/*  ADD TickerNewsManager HERE, passing the watchlist  */}
              <div style={{ padding: "24px 16px 16px 16px", backgroundColor: theme.colors.fillPrimary }}>
                <Typography variant="strap" type="utility">In the News</Typography>
              </div>
              <TickerNewsManager watchlist={watchlist} />
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default WatchlistPage;
